/*!
  _   _  ___  ____  ___ ________  _   _   _   _ ___   ____  ____   ___  
 | | | |/ _ \|  _ \|_ _|__  / _ \| \ | | | | | |_ _| |  _ \|  _ \ / _ \ 
 | |_| | | | | |_) || |  / / | | |  \| | | | | || |  | |_) | |_) | | | |
 |  _  | |_| |  _ < | | / /| |_| | |\  | | |_| || |  |  __/|  _ <| |_| |
 |_| |_|\___/|_| \_\___/____\___/|_| \_|  \___/|___| |_|   |_| \_\\___/ 
                                                                                                                                                                                                                                                                                                                                       
=========================================================
* Horizon UI Dashboard PRO - v1.0.0
=========================================================

* Product Page: https://www.horizon-ui.com/pro/
* Copyright 2022 Horizon UI (https://www.horizon-ui.com/)

* Designed and Coded by Simmmple

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

// Chakra imports
import { Box, Button, Flex, FormControl, FormLabel, Heading, Input, useColorModeValue, Text, useToast } from '@chakra-ui/react';
import React from 'react';

// Custom components
import DefaultAuth from 'layouts/auth/variants/Default';
import AuthApi from 'api/auth';

// Assets
import illustration from 'assets/img/auth/auth.png';

function ForgotPassword() {
	// Chakra color mode
	const textColor = useColorModeValue('navy.700', 'white');
	const textColorSecondary = 'gray.400';
	const brandStars = useColorModeValue('brand.500', 'brand.400');
	const toast = useToast();
	const [email, setEmail] = React.useState('');
	const [isLoading, setIsLoading] = React.useState(false);
	const [enviado, setEnviado] = React.useState(false);

	const handleSubmit = async () => {
		if (!email) {
			toast({
				title: 'Correo requerido',
				description: 'Ingresa tu correo electrónico',
				status: 'warning',
				duration: 3000,
				isClosable: true,
			});
			return;
		}
		setIsLoading(true);
		try {
			await AuthApi.ForgotPassword(email);
			setEnviado(true);
			toast({
				title: 'Enlace enviado',
				description: 'Si el correo existe en el sistema, recibirás un enlace para restablecer tu contraseña.',
				status: 'success',
				duration: 5000,
				isClosable: true,
			});
		} catch (error) {
			toast({
				title: 'Error',
				description: 'Ocurrió un error al procesar la solicitud. Intenta de nuevo.',
				status: 'error',
				duration: 4000,
				isClosable: true,
			});
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<DefaultAuth illustrationBackground={illustration} image={illustration}>
			<Flex
				w='100%'
				maxW='max-content'
				mx={{ base: 'auto', lg: '0px' }}
				me='auto'
				h='100%'
				alignItems='start'
				justifyContent='center'
				mb={{ base: '30px', md: '60px', lg: '100px', xl: '60px' }}
				px={{ base: '25px', md: '0px' }}
				mt={{ base: '40px', lg: '16vh', xl: '22vh' }}
				flexDirection='column'>
				<Box me='auto' mb='34px'>
					<Heading color={textColor} fontSize={{ base: '3xl', md: '36px' }} mb='16px'>
						Olvidaste tu contraseña?
					</Heading>
					<Text color={textColorSecondary} fontSize='md' w={{ base: '100%', lg: '456px' }} maxW='100%'>
						No hay problema. Simplemente ingresa tu correo electrónico y te enviaremos un enlace para restablecer tu contraseña.
					</Text>
				</Box>
				<Flex
					zIndex='2'
					direction='column'
					w={{ base: '100%', lg: '456px' }}
					maxW='100%'
					background='transparent'
					borderRadius='15px'
					mx={{ base: 'auto', lg: 'unset' }}
					me='auto'
					mb={{ base: '20px', md: 'auto' }}
					align='start'>
					<FormControl>
						<FormLabel display='flex' ms='4px' fontSize='sm' fontWeight='500' color={textColor} mb='8px'>
							Correo Electrónico<Text color={brandStars}>*</Text>
						</FormLabel>
						<Input
							isRequired={true}
							variant='auth'
							fontSize='sm'
							type='email'
							placeholder='ejemplo@velasresorts.com'
							mb='24px'
							size='lg'
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
						/>
						<Button
							fontSize='sm'
							variant='brand'
							fontWeight='500'
							w='100%'
							h='50'
							mb='24px'
							onClick={handleSubmit}
							isLoading={isLoading}
							loadingText='Enviando...'
						>
							{enviado ? 'Reenviar enlace' : 'Enviar enlace de restablecimiento de contraseña'}
						</Button>
					</FormControl>
				</Flex>
			</Flex>
		</DefaultAuth>
	);
}

export default ForgotPassword;
